class VerticalScrollIndicator {
  constructor() {
    this.indicator = document.getElementById('vertical-scroll-indicator');
    this.items = document.querySelectorAll('.vertical-scroll-indicator__item');
    this.galleryRows = [];
    this.currentRow = 0;
    this.isInitialized = false;
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupIndicator());
    } else {
      this.setupIndicator();
    }
  }
  
  setupIndicator() {
    // Find gallery elements
    const galleryWrapper = document.querySelector('.product__media-wrapper');
    const galleryList = document.querySelector('.product__media-list');
    
    if (!galleryWrapper || !galleryList) {
      console.log('Gallery not found, hiding vertical scroll indicator');
      this.hideIndicator();
      return;
    }
    
    // Find gallery items
    const galleryItems = Array.from(galleryList.querySelectorAll('.product__media-item'));
    
    if (galleryItems.length === 0) {
      console.log('No gallery items found, hiding vertical scroll indicator');
      this.hideIndicator();
      return;
    }
    
    // Group items into rows based on their position
    this.galleryRows = this.groupItemsIntoRows(galleryItems);
    
    console.log('Vertical scroll indicator: Found', this.galleryRows.length, 'rows');
    
    // Update indicator to match number of rows
    this.updateIndicatorItems();
    
    // Bind events
    this.bindEvents();
    
    // Initial update
    this.updateActiveRow();
    
    this.isInitialized = true;
  }
  
  groupItemsIntoRows(items) {
    const rows = [];
    
    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      const top = Math.round(rect.top);
      
      // Find existing row with similar top position (within 10px)
      let existingRow = rows.find(row => Math.abs(row.top - top) <= 10);
      
      if (existingRow) {
        existingRow.items.push(item);
      } else {
        rows.push({
          top: top,
          items: [item]
        });
      }
    });
    
    // Sort rows by top position
    rows.sort((a, b) => a.top - b.top);
    
    return rows;
  }
  
  updateIndicatorItems() {
    if (!this.indicator) return;
    
    const container = this.indicator.querySelector('.vertical-scroll-indicator__container');
    if (!container) return;
    
    // Clear existing items
    container.innerHTML = '';
    
    // Create items for each row
    this.galleryRows.forEach((row, index) => {
      const item = document.createElement('div');
      item.className = 'vertical-scroll-indicator__item';
      item.setAttribute('data-row', index);
      item.setAttribute('role', 'button');
      item.setAttribute('aria-label', `Row ${index + 1}`);
      item.setAttribute('tabindex', '0');
      
      // Add click handler
      item.addEventListener('click', () => this.scrollToRow(index));
      
      // Add keyboard navigation
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.scrollToRow(index);
        }
      });
      
      container.appendChild(item);
    });
    
    // Update items reference
    this.items = container.querySelectorAll('.vertical-scroll-indicator__item');
  }
  
  bindEvents() {
    // Listen for scroll events on the gallery
    const galleryList = document.querySelector('.product__media-list');
    if (galleryList) {
      galleryList.addEventListener('scroll', this.throttle(() => {
        this.updateActiveRow();
      }, 100), { passive: true });
    }
    
    // Listen for window resize to recalculate rows
    window.addEventListener('resize', this.throttle(() => {
      this.setupIndicator();
    }, 250), { passive: true });
    
    // Listen for gallery changes (if using AJAX)
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.setupIndicator(), 100);
    });
  }
  
  updateActiveRow() {
    if (!this.isInitialized || this.galleryRows.length === 0) return;
    
    const galleryList = document.querySelector('.product__media-list');
    if (!galleryList) return;
    
    const scrollTop = galleryList.scrollTop;
    const containerHeight = galleryList.clientHeight;
    const scrollCenter = scrollTop + containerHeight / 2;
    
    // Find which row is most visible
    let activeRowIndex = 0;
    let maxVisibleArea = 0;
    
    this.galleryRows.forEach((row, index) => {
      const firstItem = row.items[0];
      if (!firstItem) return;
      
      const itemRect = firstItem.getBoundingClientRect();
      const containerRect = galleryList.getBoundingClientRect();
      
      // Calculate visible area of this row
      const visibleTop = Math.max(itemRect.top, containerRect.top);
      const visibleBottom = Math.min(itemRect.bottom, containerRect.bottom);
      const visibleArea = Math.max(0, visibleBottom - visibleTop);
      
      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        activeRowIndex = index;
      }
    });
    
    // Update active indicator
    if (activeRowIndex !== this.currentRow) {
      this.setActiveRow(activeRowIndex);
      this.currentRow = activeRowIndex;
    }
  }
  
  setActiveRow(rowIndex) {
    this.items.forEach((item, index) => {
      item.classList.toggle('active', index === rowIndex);
    });
  }
  
  scrollToRow(rowIndex) {
    if (rowIndex < 0 || rowIndex >= this.galleryRows.length) return;
    
    const galleryList = document.querySelector('.product__media-list');
    if (!galleryList) return;
    
    const row = this.galleryRows[rowIndex];
    const firstItem = row.items[0];
    if (!firstItem) return;
    
    // Calculate scroll position to center the row
    const itemRect = firstItem.getBoundingClientRect();
    const containerRect = galleryList.getBoundingClientRect();
    const scrollTop = galleryList.scrollTop;
    
    const targetScrollTop = scrollTop + (itemRect.top - containerRect.top) - (containerRect.height - itemRect.height) / 2;
    
    galleryList.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
  }
  
  hideIndicator() {
    if (this.indicator) {
      this.indicator.style.display = 'none';
    }
  }
  
  showIndicator() {
    if (this.indicator) {
      this.indicator.style.display = 'block';
    }
  }
  
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// Initialize vertical scroll indicator
let verticalScrollIndicator = null;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  verticalScrollIndicator = new VerticalScrollIndicator();
});

// Also initialize if DOM is already loaded
if (document.readyState !== 'loading') {
  verticalScrollIndicator = new VerticalScrollIndicator();
}

// Export for potential external use
window.VerticalScrollIndicator = VerticalScrollIndicator;
